import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Search,
  X,
  Loader2,
  AlertCircle,
  ChevronDown,
  User as UserIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { pingAPI } from "@/lib/ping-api";
import type { User } from "@/types";
import type { PatientSearchResult } from "@/types/treatment-overview";

// Union type to support both User and PatientSearchResult
type Patient = User | PatientSearchResult;

const getUserInitials = (firstName: string, lastName: string): string =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

interface PatientSearchDropdownProps {
  selectedPatient: Patient | null;
  onPatientSelect: (patient: Patient | null) => void;
  placeholder?: string;
  className?: string;
  onSearch?: (query: string) => void;
  searchResults?: Patient[];
  isSearching?: boolean;
}

export const PatientSearchDropdown = React.memo(
  ({
    selectedPatient,
    onPatientSelect,
    placeholder = "Search by patient name or ID",
    className,
    onSearch,
    searchResults: externalResults,
    isSearching: externalLoading,
  }: PatientSearchDropdownProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [internalPatients, setInternalPatients] = useState<Patient[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const patients =
      externalResults !== undefined ? externalResults : internalPatients;
    const loading = externalLoading !== undefined ? externalLoading : isLoading;

    const fetchPatients = useCallback(
      async (query: string = "") => {
        if (onSearch) return;
        setIsLoading(true);
        setError(null);
        try {
          const result = await pingAPI.users.searchPatients(query);
          setInternalPatients(result || []);
        } catch {
          setError("Failed to load patients");
          setInternalPatients([]);
        } finally {
          setIsLoading(false);
        }
      },
      [onSearch]
    );

    useEffect(() => {
      if (isOpen && !onSearch) fetchPatients(debouncedSearchQuery);
    }, [debouncedSearchQuery, isOpen, fetchPatients, onSearch]);

    useEffect(() => {
      if (
        isOpen &&
        !onSearch &&
        internalPatients.length === 0 &&
        !searchQuery
      ) {
        fetchPatients("");
      }
    }, [isOpen, internalPatients.length, searchQuery, fetchPatients, onSearch]);

    useEffect(() => {
      if (onSearch && isOpen) onSearch(searchQuery);
    }, [searchQuery, onSearch, isOpen]);

    const handlePatientSelect = useCallback(
      (patient: Patient) => {
        onPatientSelect(patient);
        setIsOpen(false);
        setSearchQuery("");
      },
      [onPatientSelect]
    );

    const handleClearSelection = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onPatientSelect(null);
      },
      [onPatientSelect]
    );

    const filteredPatients = useMemo(() => {
      if (!searchQuery.trim()) return patients;
      const query = searchQuery.toLowerCase();
      return patients.filter(
        (p) =>
          p.first_name.toLowerCase().includes(query) ||
          p.last_name.toLowerCase().includes(query) ||
          p.phone_number.includes(query) ||
          (p.email && p.email.toLowerCase().includes(query)) ||
          ((p as PatientSearchResult).zarity_user_id &&
            (p as PatientSearchResult).zarity_user_id
              .toLowerCase()
              .includes(query))
      );
    }, [patients, searchQuery]);

    const displayPatients = filteredPatients;

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {/* Pill-style trigger — matches SearchInput visual language */}
          <div
            className={cn(
              "flex h-10 w-full cursor-pointer items-center rounded-full px-4",
              "border border-transparent bg-muted",
              "transition-[background-color,border-color,box-shadow]",
              isOpen &&
                "border-border/80 bg-background ring-2 ring-ring/60 ring-offset-0",
              selectedPatient && !isOpen && "bg-muted hover:bg-muted/80",
              !selectedPatient && !isOpen && "hover:bg-muted/80",
              className
            )}
            data-patient-dropdown
          >
            {selectedPatient && !isOpen ? (
              /* Selected state */
              <>
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
                  {getUserInitials(
                    selectedPatient.first_name,
                    selectedPatient.last_name
                  )}
                </div>
                <span className="z-body-md mx-2.5 min-w-0 flex-1 truncate text-foreground">
                  {selectedPatient.first_name} {selectedPatient.last_name}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 flex-shrink-0 p-0 text-muted-foreground/60 hover:text-foreground hover:bg-transparent"
                  onClick={handleClearSelection}
                  aria-label="Clear patient"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              /* Search state */
              <>
                <Search className="h-4 w-4 flex-shrink-0 text-muted-foreground/70" />
                {isOpen ? (
                  <input
                    type="text"
                    placeholder="Search patients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="z-body mx-2.5 min-w-0 flex-1 bg-transparent border-none outline-none placeholder:text-muted-foreground/80 text-foreground"
                    autoFocus
                  />
                ) : (
                  <span className="z-body mx-2.5 flex-1 truncate text-muted-foreground/90">
                    {placeholder}
                  </span>
                )}
                {loading ? (
                  <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-muted-foreground/60" />
                ) : (
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 flex-shrink-0 text-muted-foreground/60 transition-transform",
                      isOpen && "rotate-180"
                    )}
                  />
                )}
              </>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 border-border bg-popover shadow-md"
          align="start"
        >
          <div className="max-h-72 overflow-y-auto rounded-xl">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="z-secondary ml-2 text-muted-foreground">
                  Searching...
                </span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8 text-destructive">
                <AlertCircle className="h-4 w-4 mr-2" />
                <span className="z-secondary">{error}</span>
              </div>
            ) : displayPatients.length === 0 ? (
              <div className="py-8 text-center">
                <UserIcon className="h-7 w-7 mx-auto mb-2 text-muted-foreground/40" />
                <p className="z-secondary text-muted-foreground">
                  {searchQuery ? "No patients found" : "No patients available"}
                </p>
              </div>
            ) : (
              <div className="py-1">
                {displayPatients.map((patient) => (
                  <div
                    key={patient.id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handlePatientSelect(patient)}
                  >
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                      {getUserInitials(patient.first_name, patient.last_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="z-body-md text-foreground truncate">
                        {patient.first_name} {patient.last_name}
                      </div>
                      {(patient.age !== undefined || patient.gender) && (
                        <div className="z-caption text-muted-foreground/70 truncate">
                          {patient.age !== undefined && `Age ${patient.age}`}
                          {patient.age !== undefined && patient.gender && " · "}
                          {patient.gender}
                          {(patient as PatientSearchResult).zarity_user_id &&
                            ` · ${(patient as PatientSearchResult).zarity_user_id}`}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

PatientSearchDropdown.displayName = "PatientSearchDropdown";
